import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import {
  User,
  Mail,
  Shield,
  Calendar,
  Key,
  Save,
  Loader2
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

export default function ProfilePage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }

    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });
      toast.success("Mot de passe modifié avec succès");
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du changement de mot de passe");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="profile-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Mon profil</h1>
        <p className="text-muted-foreground mt-1">
          Gérez vos informations personnelles et paramètres de sécurité
        </p>
      </div>

      {/* Profile Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <User className="w-5 h-5" />
            Informations personnelles
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center gap-6">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-3xl font-bold text-primary">
                {user?.full_name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <h3 className="text-xl font-semibold">{user?.full_name}</h3>
              <div className="flex items-center gap-2 text-muted-foreground mt-1">
                <Mail className="w-4 h-4" />
                {user?.email}
              </div>
              <Badge variant="secondary" className="mt-2">
                {user?.role === "admin" ? "Administrateur" : "Utilisateur"}
              </Badge>
            </div>
          </div>

          <Separator />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-muted-foreground">Nom complet</Label>
              <div className="flex items-center gap-2 p-3 rounded-lg bg-muted">
                <User className="w-4 h-4 text-muted-foreground" />
                <span>{user?.full_name}</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground">Adresse email</Label>
              <div className="flex items-center gap-2 p-3 rounded-lg bg-muted">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <span>{user?.email}</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground">Rôle</Label>
              <div className="flex items-center gap-2 p-3 rounded-lg bg-muted">
                <Shield className="w-4 h-4 text-muted-foreground" />
                <span>{user?.role === "admin" ? "Administrateur" : "Utilisateur"}</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground">Identifiant</Label>
              <div className="flex items-center gap-2 p-3 rounded-lg bg-muted font-mono text-sm">
                <Key className="w-4 h-4 text-muted-foreground" />
                <span className="truncate">{user?.id}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Security Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Sécurité
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="currentPassword">Mot de passe actuel</Label>
              <Input
                id="currentPassword"
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) =>
                  setPasswordData({ ...passwordData, currentPassword: e.target.value })
                }
                placeholder="••••••••"
                required
                data-testid="current-password-input"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="newPassword">Nouveau mot de passe</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, newPassword: e.target.value })
                  }
                  placeholder="••••••••"
                  required
                  data-testid="new-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, confirmPassword: e.target.value })
                  }
                  placeholder="••••••••"
                  required
                  data-testid="confirm-password-input"
                />
              </div>
            </div>
            <Button type="submit" disabled={loading} data-testid="save-password-btn">
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Modifier le mot de passe
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* RGPD Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Shield className="w-5 h-5 text-green-500" />
            Protection des données (RGPD)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Vos données sont stockées de manière sécurisée sur nos serveurs en France.
            Aucune donnée n'est partagée avec des tiers. L'intelligence artificielle
            utilisée pour la classification des documents fonctionne entièrement en local,
            sans aucun envoi de données vers des services externes.
          </p>
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className="text-green-600 border-green-300">
              Conforme RGPD
            </Badge>
            <Badge variant="outline" className="text-blue-600 border-blue-300">
              Hébergé en France
            </Badge>
            <Badge variant="outline" className="text-violet-600 border-violet-300">
              IA 100% locale
            </Badge>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
